import { cwd } from 'node:process';
import React from 'react';
import { BiProvider } from '@wix/cli-telemetry-react';
import { render } from '@wix/cli-ui-kit';
import { getUserInfo, LoginModeProvider } from '@wix/cli-auth';
import { I18nProvider } from '@wix/cli-i18n';
import { Command, Option } from '@commander-js/extra-typings';
import {
  createErrorHandler,
  ErrorReporterProvider,
} from '@wix/cli-error-reporting';
import { setupTestEnv } from '@wix/cli-test-overrides';
import { getErrorLogFilePath, SENTRY_DSN } from '@wix/create-app-definitions';
import {
  HttpClientProvider,
  buildHttpClientHeaders,
} from '@wix/cli-http-client-react';
import {
  createPanorama,
  PanoramaProvider,
  createCommandTransaction,
} from '@wix/cli-panorama';
import { randomUUID } from 'node:crypto';
import { determineAgent } from 'detect-agent';
import { createBiLogger } from './bi/index.js';
import { CreateAppCommand } from './components/CreateAppCommand.js';
import {
  validateUUID,
  validateGitUrl,
  validateTemplateParams,
  validateAppName,
} from './validations/index.js';
import pkgJson from '../package.json' with { type: 'json' };
import messages from './messages.json' with { type: 'json' };
import { DeprecationMessage } from './components/DeprecationMessage.js';

setupTestEnv();

const targetParentFolder = cwd();

const {
  handler: errorHandler,
  errorReporter,
  registerToProcessErrors,
} = createErrorHandler({
  dsn: SENTRY_DSN,
  release: pkgJson.version,
  debugOutputPath: getErrorLogFilePath(targetParentFolder),
});

registerToProcessErrors();

const userInfo = await getUserInfo();
if (userInfo) {
  errorReporter.setUser({ id: userInfo.userId });
}

const program = new Command()
  .name(pkgJson.name)
  .description('Create Wix apps')
  .option(
    '-t, --template <template-id>',
    'Template ID to use for the app creation',
    validateUUID
  )
  .addOption(
    new Option(
      '-r, --template-repo <template-repo>',
      'Git repository URL to fetch the template from'
    )
      .argParser(validateGitUrl)
      .hideHelp()
  )
  .addOption(
    new Option(
      '-p, --template-repo-path <template-repo-path>',
      'Path inside the Git repository to fetch the template from'
    ).hideHelp()
  )
  .addOption(
    new Option(
      '-x, --template-path <template-path>',
      'Local path to the template folder'
    ).hideHelp()
  )
  .addOption(
    new Option(
      '--app-name <app-name>',
      'The name for your new application'
    ).argParser(validateAppName)
  )
  .addOption(
    new Option(
      '--skip-install',
      'Skip automatic dependency installation'
    ).hideHelp()
  )
  .addOption(
    new Option(
      '--skip-git',
      'Prevent the application from automatically initializing a Git repository during setup'
    ).hideHelp()
  )
  .addOption(
    new Option(
      '--template-params <template-params>',
      'Additional template parameters to be provided to the app template should be formatted as a JSON string. These parameters can be accessed through the templateParams variable.'
    )
      .argParser(validateTemplateParams)
      .hideHelp()
  )
  .version(pkgJson.version, '-v, --version')
  .action(async (options, command) => {
    errorReporter.setContext('Command Info', {
      Arguments: command.args,
      Options: command.opts(),
    });

    const cliSessionId = randomUUID();
    errorReporter.setCliSessionId(cliSessionId);

    const defaultAgentDetectionResult = {
      agent: undefined,
      isAgent: false,
    } as const;

    const aiAgent = await determineAgent().catch((error: unknown) => {
      errorReporter.reportError(error);
      return defaultAgentDetectionResult;
    });

    const aiAgentName = aiAgent.agent?.name;
    if (aiAgentName != null) {
      errorReporter.setAiAgentName(aiAgentName);
    }

    const { biLogger, reportCommandStartEvent } = await createBiLogger({
      errorReporter,
      cliSessionId,
      userId: userInfo?.userId,
      aiAgentName,
    });

    const panorama = await createPanorama({
      artifact: pkgJson.wix.artifact,
      version: pkgJson.version,
      userId: userInfo?.userId,
      errorReporter,
      cliSessionId,
      aiAgentName,
    });

    const commandStatus = reportCommandStartEvent(command);

    const commandOptions = {
      ...options,
      skipInstall: options.skipInstall ?? false,
      skipGit: options.skipGit ?? false,
    };

    const headers = buildHttpClientHeaders({
      flow: 'CreateApp',
      command: command.name(),
      cliVersion: pkgJson.version,
    });

    const transaction = createCommandTransaction(panorama, command);

    const loginMode = aiAgent.isAgent ? 'agent' : 'interactive';

    try {
      transaction.start();

      await render(
        <BiProvider value={biLogger}>
          <ErrorReporterProvider value={errorReporter}>
            <PanoramaProvider value={panorama}>
              <HttpClientProvider headers={headers}>
                <I18nProvider messages={messages}>
                  <DeprecationMessage />
                  <LoginModeProvider value={loginMode}>
                    <CreateAppCommand
                      userInfo={userInfo}
                      targetParentFolder={targetParentFolder}
                      commandOptions={commandOptions}
                    />
                  </LoginModeProvider>
                </I18nProvider>
              </HttpClientProvider>
            </PanoramaProvider>
          </ErrorReporterProvider>
        </BiProvider>
      );

      transaction.finish();
      commandStatus.succeed();
    } catch (e) {
      commandStatus.failed(e);
      throw e;
    }
  });

try {
  await program.parseAsync();
} catch (error) {
  errorHandler({ error });
}
